<template>
	<LinceDropdown
        ref="refDropdownBaseSelect"
        :isFixed="isFixed"
        :isVirtualScroll="isVirtualScroll"
        :spacerHeight="Math.min(containerHeight + 50, 350)"
		:position="position"
        :isFullWidth="isFullWidth"
        :class="['select', { isFullWidth }]"
		v-bind="$attrs"
		@changeVisible="onDropdown"
	>
		<template #preview>
			<LinceButton
				:active="localOpen"
                :style="{background: previewColor}"
				class="select-preview"
			>
                <div
                    v-if="isCustomPreview"
                    class="select-preview__circle"
                >
                    <base-icon
                        icon="icon-tick"
                        size="sm"
                        color="#fff"
                        class="select-preview__circle-icon"
                    />
                </div>
                <span
                    v-if="!customTitle && isTitleColor"
                    :style="getOptionColor(titleOption)"
                    class="option-color-preview"
                />
				<span v-if="!customTitle" class="select-preview__label" :title="title">
					{{title}}
				</span>
                <div v-else class="select-preview__label-custom">
                    <span class="select-preview__label-custom-name">{{ customTitleValue }}</span>
                    <span
                        v-if="!isSimpleCustomTitle"
                        class="select-preview__label-custom-value"
                        :class="{'select-preview__label-custom-value--not-found': customTitleValue === 'Не найдено'}">{{ customTitleValue }}</span>
                </div>

				<div class="select-preview__arrow">
					<LinceIconArrowDown class="select-preview__arrow-icon"/>
				</div>
			</LinceButton>
		</template>

		<template #content>
			<div
				:style="{'width': widthDropdownContent}"
				class="select-dropdown"
			>
				<div class="select-dropdown__triangle" />
				<!-- HEADER -->
				<div v-if="$slots.header" class="select-dropdown__header">
					<div class="select-dropdown__top">
						<slot name="header" />
					</div>
				</div>

				<!-- SEARCH -->
				<div
					v-if="canSearch"
					class="select-dropdown__search"
				>
					<input
                        ref="searchInput"
						v-model="searchValue"
						type="text"
						:placeholder="searchPlaceholder"
                        @input="onSearchValue"
					>
				</div>

				<!-- OPTIONS -->
				<div
                    ref="selectOptionsContainer"
                    class="select-options"
                    @scroll="onScroll"
                >
                    <div ref="spacer" :style="getSpacerStyle()">
                        <div
                            v-for="(option, index) of virtualOptions"
                            :key="index"
                            class="select-options__item"
                            :class="getOptionClasses(option)"
                            :style="getOptionStyle()"
                            :title="isVirtualScroll ? option.value : null"
                            @click="onCheck(option)"
                        >
                            <div
                                v-if="isOptionColor(option)"
                                class="option-color-preview"
                                :style="getOptionColor(option)"
                            />
                            <span>{{option.value}}</span>
                            <div
                                v-if="getIsRequired(option)"
                                class="select-options__item_required"
                            >
                                *
                            </div>
                            <button
                                v-if="isRemoveOption"
                                class="select-options__btn-close"
                                @click.stop="onRemoveOption(option)"
                            >
                                &times;
                            </button>
                        </div>
                    </div>
				</div>

				<!-- FOOTER -->
				<div
					v-if="$slots.footer"
					class="select-dropdown__footer"
				>
					<slot name="footer" />
				</div>
			</div>
		</template>
	</LinceDropdown>
</template>

<script>
import BaseIcon from "@/components/Base/BaseIcon";
export default {
	name: "BaseSelect",
	components: { BaseIcon },
	props: {
		/**
		 * @property {Array} options - массив опций
		 * @property {Array} value - выбранное значение по умолчанию
		 */
		options: {
			type: Array,
			default: () => []
		},
		value: {
			type: [String, Number, Array],
			default: null
		},
		defaultTitle: {
			type: [String, Number],
			default: 'Выбрать значение',
		},
        customTitle: {
            type: [String, Number],
            default: null
        },
		canSearch: {
			type: Boolean,
			default: false
		},
		isVirtualSearch: {
			type: Boolean,
			default: true
		},
		isCloseAfterCheck: {
			type: Boolean,
			default: true
		},
        isFullWidth: {
            type: Boolean,
            default: false,
        },
		open: {
			type: Boolean,
			default: false
		},
        isCustomPreview: {
            type: Boolean,
            default: false
        },
        isSimpleCustomTitle: {
            type: Boolean,
            default: false
        },
        previewColor: {
            type: String,
            default: ''
        },
        isFixed: {
            type: Boolean,
            default: false
        },
        isVirtualScroll: {
            type: Boolean,
            default: false
        },
        virtualScrollConfig: {
            type: Object,
            default: () => ({})
        },
        delaySearchValueLazy: {
            type: Number,
            default: 500
        },
		position: {
			type: String,
			default: 'right',
			validator: (value) => ['right', 'left'].includes(value)
		},
		widthDropdownContent: {
			type: String,
			default: '100%'
		},
        searchPlaceholder: {
            type: String,
            default: 'Поиск'
        },
        isRemoveOption: {
            type: Boolean,
            default: false
        }
	},
	data() {
		/**
		 * @property {Boolean} localOpen - условие отображение опций
		 */
		return {
			searchValue: '',
			localOpen: this.open,
            scrollTop: 0,
            nodeCountVisible: 0,
            spacerHeight: 0,
			isLoading: false,
			lastScrollTop: 0,
            isFirstTimeOpenedDropdown: false,
            searchValueLazyTimerId: null
		}
	},
	computed: {
		/**
		 * Вернет title для селекта
		 */
		title() {
            const currentOption = this.options.find(i => i.id === this.value) || {};
			return currentOption.value || this.defaultTitle;
		},

        titleOption() {
            return this.options.find(i => i.id === this.value) ?? null;
        },

        isTitleColor(){
            return this.titleOption !== null && (this.titleOption?.color ?? null) !== null;
        },

        /**
         * Вернет выбранный option для кастомного title
         */
        customTitleValue() {
            const currentOption = this.options.find(i => i.id === this.value) || {};
            if (this.isSimpleCustomTitle) {
                return currentOption.value || this.customTitle;
            }
            if (!currentOption.value || currentOption.value === 'null') {
                return 'Не найдено';
            }
            return currentOption.value;
        },
		/**
		 * Массив значений по ключу поиска
		 */
		filteredOptions() {
            const filteredOptions = this.options.filter(item => {
				const optionValue = String(item.value).toLowerCase()
				const searchValue = String(this.searchValue).toLowerCase()

				return optionValue.startsWith(searchValue)
			})
			return this.isVirtualSearch ? filteredOptions : this.options;
		},
        virtualOptions() {
            return this.isVirtualScroll ? this.filteredOptions.slice(this.nodeIndexStart, this.nodeIndexEnd + 1) : this.filteredOptions;
        },
        optionsRequired() {
            const optionsRequired = this.options.filter(option => {
                const {required = false, requiredGroup = '', requiredType = ''} = option;
                return Boolean(required) || String(requiredGroup) !== '' || String(requiredType) !== '';
            }).map(option => {
                const {id = ''} = option;
                return String(id);
            });
            return optionsRequired;
        },
        nodeHeight() {
            const {nodeHeight = 35} = this.virtualScrollConfig;
            return nodeHeight;
        },
        nodeIndexStart() {
            const nodeIndexStart = Math.floor(this.scrollTop / this.nodeHeight) - 5;
            return Math.max(0, nodeIndexStart);
        },
        nodeIndexEnd() {
            const nodeIndexEnd = this.nodeIndexStart + 12;
            return Math.min(nodeIndexEnd, (this.filteredOptions.length - 1));
        },
        containerHeight() {
            return this.filteredOptions.length * this.nodeHeight;
        },
        spacerTopStyle() {
            const spacerTopStyle = `${this.nodeIndexStart * this.nodeHeight}px`;
            return spacerTopStyle;
        },
        spacerBottomStyle() {
            const spacerBottomStyle = `${((this.filteredOptions.length - 1) - this.nodeIndexEnd) * this.nodeHeight}px`;
            return spacerBottomStyle;
        },
		optionsLength() {
			return this.options.length
		}
	},
    watch: {
        localOpen(isLocalOpen) {
            if (this.isVirtualScroll) {
                if (isLocalOpen)
                    this.$nextTick(() => {
                        this.getNodeCountVisible();
                        this.getSpacerHeight();
                    });
                else
                    this.scrollTop = 0;
            }
        },
        filteredOptions() {
            if (this.localOpen && this.isVirtualScroll) {
                this.$refs.selectOptionsContainer.scrollTop = 0;
                this.scrollTop = 0;
            }
        },
		optionsLength() {
			this.isLoading = false
		},
    },
	created() {
		// Сработает при эмите события cancel в родителе
		this.$parent.$on('$closeSelect', this.onClose)
	},
	methods: {
        isOptionColor(option) {
            const {color = null} = option;
            return color !== null;
        },

        getOptionColor(option) {
            return {'background-color': `#${option.color}`};
        },

        onSearchValue() {
            if (this.searchValueLazyTimerId !== null) {
                clearTimeout(this.searchValueLazyTimerId);
                this.searchValueLazyTimerId = null;
            }
            this.searchValueLazyTimerId = setTimeout(() => {
                this.$emit('onSearchValue', this.searchValue);
            }, this.delaySearchValueLazy);
        },

        onScroll(event) {
            if (this.isVirtualScroll) {
                const {currentTarget = {}} = event;
                const {scrollTop = 0} = currentTarget;
                this.scrollTop = scrollTop;
            }

			this.onLazyLoad(event);
        },
		onLazyLoad(event) {
			if (this.lastScrollTop == event.currentTarget.scrollTop) { return }

            const currentTarget = event.currentTarget
            const {scrollTop, scrollHeight, clientHeight} = currentTarget
			const difference = scrollHeight - (clientHeight + scrollTop)

            if (difference < 400) {
				if (!this.isLoading) {
					this.$emit('lazyload')
				}
				this.isLoading = true
            }

			this.lastScrollTop = event.currentTarget.scrollTop
		},
        getIsRequired(option) {
            const {id = ''} = option;
            return this.optionsRequired.includes(String(id));
        },
		updateSearchValues(value){
			this.searchValue = value;
		},
        getNodeCountVisible() {
            const {selectOptionsContainer = {}} = this.$refs;
            const bounding = selectOptionsContainer.getBoundingClientRect() || {};
            const{height = 0} = bounding;
            const nodeCountVisible = Math.floor(height / this.nodeHeight);
            this.nodeCountVisible = nodeCountVisible;
        },
        getSpacerHeight() {
            this.spacerHeight = this.containerHeight;
        },
        getOptionStyle() {
            const optionStyle = {
                'white-space': 'nowrap',
                'height': `${this.nodeHeight}px`,
            };
            return this.isVirtualScroll ? optionStyle : {};
        },
        getSpacerStyle() {
            const spacerStyle = {
                'padding-top': this.spacerTopStyle,
                'padding-bottom': this.spacerBottomStyle
            };
            return this.isVirtualScroll ? spacerStyle : {};
        },
		/**
		 * @returns {Object} классы для опций
		 */
		getOptionClasses(option) {
			if (Array.isArray(this.value)) {
				return {
					'select-options__item_active': this.value.includes(option.id)
				}
			}

			return {
				'select-options__item_active': this.value === option.id
			}
		},
		/**
		 * close
		 */
		onClose() {
			this.$refs?.refDropdownBaseSelect?.close();
			this.$emit('close')
		},
		/**
		 * @param {Object} option - option по которому был совершен клик
		 */
		onCheck(option) {
			this.$emit('check', option)

			if (this.isCloseAfterCheck) {
				this.onClose()
			}
		},

        onRemoveOption(option){
            this.$emit('onRemoveOption', option)
        },

		onDropdown(event) {
            if(this.canSearch && event){
                this.$nextTick(() => {
                    this.$refs.searchInput.focus();
                });
            }

			this.localOpen = event
            this.$emit('onToggleDropdown', event)
		}
	}
}
</script>

<style lang="scss" scoped>
$primary-color: #F0F3F8 !default;
$success-color: #6EC87A !default;
$primary-disabled-color: #DCE1E8 !default;

.select {
	display: inline-block;
	position: relative;
	width: 130px;
	user-select: none;
	outline: none;
	font-size: 13px;

    &.isFullWidth {
        width: 100%;
    }

	&-preview {
		// padding: 6px 15px;
		padding: 9px 15px;
		border: 1px solid transparent;
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
		cursor: pointer;
		transition: .2s;
		text-align: left;
        &__circle {
            border-radius: 100%;
            width: 14px;
            height: 14px;
            background: #6EC87A;
            margin-right: 5px;
            &-icon {
                padding-left: 2px;
            }
        }

		&__label {
			text-overflow: ellipsis;
			white-space: nowrap;
			overflow: hidden;
			flex-grow: 1;

			font-weight: 400;
            font-size: 14px;
            line-height: 18px;
            // color: #949BA3;
		}

        &__label-custom {
            font-size: 14px;
            line-height: 20px;
            font-weight: 400;
            overflow: hidden;
            &-name {
                color: $assistant-color;
            }
            &-value {
                color: #000;
                margin-left: 5px;
                font-weight: 700;

                &--not-found {
                    color: #F64A4A;
                }
            }
        }

		&__arrow {
			img { width: 100%; }

			&-icon {
                width: 15px;
                height: 15px;
            }
		}
	}

	&-dropdown {
        height: 100%;
		max-height: 290px;
		overflow: hidden;
		display: flex;
		flex-direction: column;

		&__search {
			width: 100%;
			cursor: pointer;
			border-radius: inherit;
			transition: .1s;

			input {
				border: none;
				border-bottom: 1px solid $primary-disabled-color;
				background: transparent;
				height: 34px;
				width: 100%;
				display: block;
				outline: none;
				padding-left: 16px;

				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath id='iconmonstr-magnifier-2' d='M10,9.019,7.394,6.434a4.012,4.012,0,0,0,.78-2.38A4.087,4.087,0,0,0,0,4.054,4.092,4.092,0,0,0,6.391,7.4L9.011,10,10,9.019ZM1.2,4.054A2.888,2.888,0,1,1,4.087,6.92,2.88,2.88,0,0,1,1.2,4.054Z' fill='%23b9bbc2'/%3E%3C/svg%3E");
				background-repeat: no-repeat;
				background-position: center left;
				background-size: 10px;
				font-size: 12px;

				&::placeholder {
					color: $primary-disabled-color !important;
				}
			}
		}

		&__header {
			flex-shrink: 0;
			flex-grow: 0;
		}
		&__top {
			margin-bottom: 4px;
		}
		&__footer {
			flex-shrink: 0;
			flex-grow: 0;
			margin-top: 20px;
		}
	}

	&-options {
		flex-grow: 1;
		overflow: auto;
		margin-top: 10px;
		padding-right: 10px;

		&__item {
            position: relative;
			border: 1px solid transparent;
			max-width: 100%;
			cursor: pointer;
			border-radius: 400px;
			transition: .1s;
			padding: 8px 15px;
            display: flex;
            &_required {
                color: #F84967;
            }

			&:hover:not(.select-options__item_active) {
				background: $primary-color;
			}

			span {
				// white-space: nowrap;
				display: block;
				width: 90%;
				text-overflow: ellipsis;
				// white-space: nowrap;
				overflow: hidden;
			}

			&_active {
				border: 1px solid $success-color;
				color: $success-color;
			}
		}

        &__btn-close {
            position: absolute;
            top: 4px;
            right: 8px;
            font-size: 12px;
            color: #DCE1E8;
            transition: all 0.4s ease-out;
            padding: 5px;

            &:hover {
                color: #000;
            }
        }
	}
}

.option-color-preview {
    border-radius: 100%;
    width: 14px;
    height: 14px;
    margin-right: 5px;
}
</style>
